/**
 * Termos de Aceite — Sistema de Centros de Treinamento Meiuca (B2B).
 *
 *  RASCUNHO PRELIMINAR — REVISÃO JURÍDICA OBRIGATÓRIA antes da publicação oficial.
 *
 * Documento contratual entre a Meiuca (operadora) e o gestor do Centro de
 * Treinamento (CT) que adere ao Sistema de Centros de Treinamento. Adere ao
 * modelo BaaS regulado pela Resolução Conjunta nº 16/2025 do BACEN: o ASAAS
 * GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A. é a instituição financeira
 * responsável pelo processamento dos pagamentos; a Meiuca atua como integradora
 * tecnológica (Tomador BaaS).
 *
 * Endpoint público esperado: https://meiucaapp.com.br/centros-de-treinamento/termos
 *
 * Migrado de `meiuca-site` (Next.js) para `meiuca-app-web-react` (React Router).
 */

import { Body, H2, H3 } from '@meiuca/ui-components'
import { LegalPageLayout } from '../components/LegalPageLayout'

const ctRelatedLinks = [
  { href: '/centros-de-treinamento/termos', label: 'Termos do Sistema de CT' },
  { href: '/centros-de-treinamento/privacidade', label: 'Privacidade do Sistema de CT' },
  { href: '/termos', label: 'Termos do app Meiuca (consumidor)' }
]

export function CentrosDeTreinamentoTermosPage() {
  return (
    <LegalPageLayout
      title="Termos de Aceite — Centros de Treinamento"
      documentTitle="Termos de Aceite — Centros de Treinamento — Meiuca"
      lastUpdated="20 de maio de 2026"
      relatedLinks={ctRelatedLinks}
    >
      <Body>
        Estes Termos de Aceite regulam o acesso e uso do{' '}
        <strong>Sistema de Centros de Treinamento</strong> oferecido pela Meiuca a
        professores, escolas, clubes e profissionais do esporte que prestam serviços
        de treinamento. Ao criar uma conta de operador, ativar um Centro de Treinamento
        ou utilizar qualquer funcionalidade do sistema, você declara que leu, entendeu
        e concorda integralmente com estas condições e que possui poderes para vincular
        a pessoa física ou jurídica titular do Centro de Treinamento.
      </Body>

      <section>
        <H2 className="text-xl md:text-2xl mt-8 mb-3">1. Definições</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            <strong>Meiuca</strong>: <em>Rafael Balmant Letro — ME</em>, inscrita no CNPJ
            sob nº 27.422.545/0001-42, sede na Rua São Marcos, 370 — Bairro São Vito —
            Americana/SP — CEP 13472-200, operadora do Sistema de Centros de Treinamento
            e Tomador BaaS do ASAAS.
          </li>
          <li>
            <strong>Centro de Treinamento</strong> ou <strong>CT</strong>: pessoa física
            ou jurídica que oferece atividades de treinamento esportivo (aulas, turmas,
            programas) e adere ao Sistema para gestão de alunos, contratos, mensalidades
            e recebimentos.
          </li>
          <li>
            <strong>Sistema de Centros de Treinamento</strong>: conjunto de funcionalidades
            web e APIs disponibilizadas pela Meiuca para o CT gerir alunos, membros,
            turmas, contratos, locais de treino, ocorrências, cobranças e relatórios.
          </li>
          <li>
            <strong>App Meiuca</strong>: aplicativo mobile direcionado ao consumidor final
            (Aluno), por meio do qual planos e treinos podem ser visualizados e pagos.
          </li>
          <li>
            <strong>ASAAS</strong>: <em>ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS
            S.A.</em>, CNPJ 19.540.550/0001-21, instituição de pagamento autorizada pelo
            Banco Central do Brasil (BACEN), prestadora dos serviços financeiros.
          </li>
          <li>
            <strong>Subconta</strong>: conta de pagamento aberta em nome do CT no ASAAS
            por meio do Sistema, na qual os valores pagos pelos Alunos são creditados.
          </li>
          <li>
            <strong>Aluno</strong> ou <strong>Cliente Final</strong>: pessoa física que
            contrata e paga atividades de treinamento pela plataforma Meiuca.
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          2. Modelo de negócio e prestação dos serviços financeiros (BaaS)
        </H2>
        <Body>
          A Meiuca opera no modelo <strong>Banking as a Service (BaaS)</strong>, regulado
          pela Resolução Conjunta nº 16/2025 do Banco Central do Brasil. Nesse modelo:
        </Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-3">
          <li>
            Os serviços financeiros e de pagamentos disponibilizados por meio do Sistema
            de Centros de Treinamento — incluindo abertura e manutenção de subconta de
            pagamento, processamento de transações Pix, emissão de boletos (quando
            aplicável), cobranças recorrentes (mensalidades), transferências, refunds
            e demais movimentações de valores — são prestados pelo{' '}
            <strong>
              ASAAS GESTÃO FINANCEIRA INSTITUIÇÃO DE PAGAMENTOS S.A.
            </strong>
            , instituição de pagamento autorizada a funcionar pelo Banco Central do Brasil.
          </li>
          <li>
            A <strong>Meiuca</strong> atua exclusivamente como{' '}
            <strong>integradora tecnológica e distribuidora da experiência do produto</strong>,
            não sendo instituição financeira ou de pagamento, nem realizando intermediação
            financeira em nome próprio.
          </li>
          <li>
            O CT declara ciência de que o relacionamento financeiro/de pagamentos e a
            responsabilidade regulatória pelos serviços acima descritos são do ASAAS,
            nos termos da regulamentação vigente.
          </li>
          <li>
            Os valores pagos pelos Alunos são creditados <strong>diretamente</strong> na
            Subconta do CT no ASAAS; a Meiuca não detém, custodia ou movimenta o dinheiro
            dos clientes finais em nome próprio.
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">3. Cadastro e elegibilidade</H2>
        <Body>
          Para acessar o Sistema de Centros de Treinamento, o CT deve fornecer dados
          verdadeiros, atualizados e completos. A Meiuca pode, a seu critério, recusar
          cadastros, solicitar documentação adicional ou bloquear o acesso de CTs que não
          atendam aos requisitos legais, regulatórios ou de integridade descritos nestes
          Termos.
        </Body>
        <Body className="mt-3">
          A pessoa que aceita estes Termos declara que possui plenos poderes para vincular
          o CT titular e que tem capacidade civil e legal para tanto.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          4. Subconta no ASAAS e processo KYC
        </H2>
        <H3 className="text-lg mt-4 mb-2">4.1 Abertura</H3>
        <Body>
          Para receber mensalidades e cobranças de Alunos via Pix, o CT deverá completar
          o fluxo de abertura de Subconta no ASAAS, fornecendo os dados comerciais
          (CPF/CNPJ, razão social/nome completo, e-mail e telefone de cobrança, data de
          nascimento — quando PF —, renda/faturamento mensal) e o endereço completo
          solicitados nas configurações do Sistema. Esses dados são transmitidos ao
          ASAAS para cumprimento das exigências de KYC, PLD/FT e demais obrigações
          regulatórias do BACEN.
        </Body>

        <H3 className="text-lg mt-6 mb-2">4.2 Aprovação e recusa</H3>
        <Body>
          A aprovação da Subconta depende exclusivamente do ASAAS. A Meiuca não pode
          garantir nem antecipar o resultado da análise. Se o ASAAS recusar a Subconta,
          informaremos o CT com a razão fornecida pelo ASAAS, quando disponível, e
          orientações para correção.
        </Body>

        <H3 className="text-lg mt-6 mb-2">4.3 Manutenção dos dados</H3>
        <Body>
          O CT se obriga a manter os dados da Subconta atualizados. Após a aprovação,
          alterações de CPF, CNPJ, razão social, tipo de conta ou data de nascimento
          exigirão a abertura de nova Subconta no ASAAS. Demais alterações (e-mail,
          telefone, endereço) são processadas mediante solicitação ao suporte da Meiuca.
        </Body>

        <H3 className="text-lg mt-6 mb-2">4.4 Vedação a múltiplos provedores</H3>
        <Body>
          Por exigência do BACEN, o CT <strong>não pode</strong> utilizar mais de um
          provedor BaaS simultaneamente para abertura de contas e serviços de pagamento
          associados a uma mesma operação no marketplace Meiuca.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          5. Mensalidades, contratos e cobranças recorrentes
        </H2>
        <Body>
          O CT define contratos, planos e ciclos de cobrança no Sistema. As cobranças
          (Pix ou boleto, conforme disponibilidade no ASAAS) são geradas automaticamente
          a cada ciclo e enviadas aos Alunos correspondentes. Os valores caem na Subconta
          do CT no ASAAS, mediante split automático que destaca a tarifa devida à Meiuca,
          nos termos da cláusula 8.
        </Body>
        <Body className="mt-3">
          O CT é responsável pela definição correta de valores, datas de início,
          renovação automática, política de inadimplência e demais regras contratuais
          aplicáveis ao Aluno.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">6. Saques (withdraws)</H2>
        <Body>
          O CT pode solicitar saques do saldo disponível em sua Subconta para uma chave
          Pix de titularidade própria, cadastrada no Sistema. A taxa de transferência
          (gateway fee) cobrada pelo ASAAS é descontada do valor solicitado.
        </Body>
        <Body className="mt-3">
          Saques estão sujeitos aos prazos e regras operacionais do ASAAS. Em caso de
          falha ou estorno do saque, o valor é restituído ao saldo disponível da Subconta
          e o CT é notificado pelo Sistema.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          7. Cancelamentos, reembolsos e ocorrências
        </H2>
        <Body>
          A política de cancelamento de contratos, suspensão de mensalidades e reembolsos
          é definida pelo CT no Sistema e deve ser comunicada ao Aluno no momento da
          contratação. Reembolsos (parciais ou totais) são executados pelo ASAAS por
          instrução da Meiuca, conforme a regra configurada, e debitados da Subconta do CT.
        </Body>
        <Body className="mt-3">
          O Sistema oferece módulo de <strong>ocorrências</strong> para registro de fatos
          relevantes envolvendo Alunos (faltas, advertências, suspensões). Esses registros
          devem ser feitos com observância à LGPD e ao Código Civil, evitando juízos de
          valor desnecessários e atendendo ao princípio da finalidade.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">8. Tarifas, split e taxas</H2>
        <Body>
          A Meiuca cobra pela utilização do Sistema mediante tarifa percentual (split)
          aplicada sobre cada cobrança paga via marketplace. O valor da tarifa é informado
          em proposta comercial específica e refletido na configuração da Subconta no
          momento do split automático realizado pelo ASAAS.
        </Body>
        <Body className="mt-3">
          Adicionalmente, o ASAAS pode cobrar tarifas próprias pelo processamento e pelos
          saques. <strong>A Meiuca não cobra tarifas financeiras (Pix, conta, saque) em
          nome próprio</strong>; tais tarifas são da exclusiva responsabilidade e
          prerrogativa do ASAAS, conforme exigência do BACEN.
        </Body>
        <Body className="mt-3">
          O CT reconhece e concorda com o split automático aplicado pelo ASAAS sobre cada
          cobrança liquidada no marketplace.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">9. Responsabilidades do CT</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            Cumprir todas as obrigações legais, fiscais, trabalhistas e regulatórias
            aplicáveis à atividade de treinamento esportivo, incluindo registro do
            profissional no respectivo conselho (CREF, quando aplicável), seguros e
            recolhimento de tributos.
          </li>
          <li>
            Manter dados cadastrais e fiscais sempre atualizados no Sistema e na Subconta
            do ASAAS.
          </li>
          <li>
            Garantir consentimento e bases legais adequadas para o tratamento dos dados
            pessoais dos Alunos, conforme a LGPD.
          </li>
          <li>
            <strong>
              Não se apresentar como instituição financeira ou de pagamento
            </strong>{' '}
            em qualquer canal próprio. Por exigência do BACEN, o CT não pode utilizar
            termos como &ldquo;Bank&rdquo;, &ldquo;Banco&rdquo;, &ldquo;Pay&rdquo;,
            &ldquo;Payments&rdquo;, &ldquo;Financeira&rdquo;, &ldquo;Wallet&rdquo; ou
            &ldquo;Instituição de Pagamento&rdquo; em sua nomenclatura ou comunicação ao
            mercado se não possuir a devida autorização.
          </li>
          <li>
            Não cobrar tarifas financeiras (Pix, conta, saque) em nome próprio. O CT pode
            cobrar pelo seu serviço de treinamento e essas tarifas seguem sendo dele,
            desde que fique claro ao Aluno que a <em>operação financeira</em> é realizada
            pelo ASAAS.
          </li>
          <li>
            Permitir e respeitar a aplicação visível do selo do ASAAS nos fluxos de
            pagamento dentro do Sistema e do app do Aluno, conforme determina o Playbook
            BaaS do ASAAS.
          </li>
          <li>
            Não revender, sublicenciar ou disponibilizar a infraestrutura financeira do
            ASAAS a terceiros sem autorização expressa da Meiuca e do próprio ASAAS.
          </li>
          <li>
            Atender com diligência as solicitações dos Alunos relativas à atividade
            esportiva (entrada na turma, ajustes de horário, conteúdo).
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">10. Responsabilidades da Meiuca</H2>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary">
          <li>
            Manter o Sistema disponível e funcional, dentro de padrões razoáveis de
            mercado, e prestar suporte técnico via canais oficiais.
          </li>
          <li>
            Aplicar a marca do ASAAS de forma visível e em conformidade com o Playbook
            BaaS em todos os fluxos relevantes.
          </li>
          <li>
            Compartilhar com o ASAAS apenas os dados estritamente necessários para o
            cumprimento das obrigações regulatórias e o processamento financeiro.
          </li>
          <li>
            Informar o CT sobre eventos relevantes na Subconta (aprovação, recusa,
            falhas, webhooks de pagamento e saque).
          </li>
          <li>
            Não alterar nem criar documentos legais relacionados ao processamento
            financeiro de pagamentos, que são de competência exclusiva do ASAAS.
          </li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">11. Responsabilidades do ASAAS</H2>
        <Body>
          O ASAAS é responsável pela segurança da operação financeira de pagamentos,
          incluindo PLD/FT, KYC e prevenção à fraude, ainda que a jornada se inicie na
          interface do Sistema. O ASAAS responde pela custódia dos valores, liquidação,
          comprovação fiscal, retenções legais e suporte regulatório, conforme os termos
          e condições próprios do ASAAS disponíveis em{' '}
          <a
            href="https://asaas.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary hover:underline"
          >
            asaas.com
          </a>
          .
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">12. Suporte</H2>
        <Body>
          <strong>Suporte ao Sistema (produto Meiuca):</strong>{' '}
          <a
            href="mailto:suporte@meiuca.com.br"
            className="text-brand-primary hover:underline"
          >
            suporte@meiuca.com.br
          </a>
          .
        </Body>
        <Body className="mt-3">
          <strong>Suporte ao processamento financeiro (ASAAS):</strong> 0800 009 0037
          (somente pessoa jurídica, voz e mensagens) e{' '}
          <a
            href="mailto:contato@asaas.com.br"
            className="text-brand-primary hover:underline"
          >
            contato@asaas.com.br
          </a>
          . O CT deve garantir que o Aluno saiba que pode acionar o ASAAS para suporte
          relacionado às operações financeiras de pagamentos.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">
          13. Tratamento de dados pessoais (LGPD)
        </H2>
        <Body>
          O tratamento dos dados pessoais coletados no Sistema é descrito na{' '}
          <a
            href="/centros-de-treinamento/privacidade"
            className="text-brand-primary hover:underline"
          >
            Política de Privacidade do Sistema de Centros de Treinamento
          </a>
          , que integra estes Termos. O CT reconhece a necessidade de compartilhamento
          de dados com o ASAAS para cumprimento das exigências regulatórias e declara
          que obteve as bases legais necessárias para o tratamento dos dados de seus
          Alunos transmitidos à Meiuca.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">14. Vigência, suspensão e rescisão</H2>
        <Body>
          Estes Termos vigoram por prazo indeterminado a partir do aceite. O CT pode
          rescindir a qualquer momento mediante solicitação à Meiuca, ressalvado o
          cumprimento de obrigações financeiras pendentes na Subconta do ASAAS e o
          encerramento ordenado dos contratos com Alunos.
        </Body>
        <Body className="mt-3">
          A Meiuca pode suspender ou rescindir imediatamente a adesão do CT em caso de:
        </Body>
        <ul className="list-disc pl-6 space-y-2 text-text-secondary mt-2">
          <li>Violação destes Termos ou dos termos do ASAAS;</li>
          <li>Fraude, suspeita de fraude ou utilização indevida da plataforma;</li>
          <li>Determinação do ASAAS, do BACEN ou de autoridade competente;</li>
          <li>Risco à integridade da plataforma ou de terceiros.</li>
        </ul>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">15. Modificações</H2>
        <Body>
          A Meiuca pode atualizar estes Termos a qualquer momento para refletir
          mudanças regulatórias, comerciais ou operacionais. Mudanças relevantes serão
          comunicadas por e-mail ou por aviso no Sistema com antecedência razoável.
          O uso continuado da plataforma após a comunicação implica concordância com a
          nova versão.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">16. Lei aplicável e foro</H2>
        <Body>
          Estes Termos são regidos pelas leis da República Federativa do Brasil. Fica
          eleito o foro da Comarca de Americana/SP para dirimir qualquer controvérsia
          decorrente destes Termos, com renúncia expressa a qualquer outro, por mais
          privilegiado que seja.
        </Body>
      </section>

      <section>
        <H2 className="text-xl md:text-2xl mt-10 mb-3">17. Contato</H2>
        <Body>
          Dúvidas, sugestões ou reclamações sobre estes Termos:{' '}
          <a
            href="mailto:contratos@meiuca.com.br"
            className="text-brand-primary hover:underline"
          >
            contratos@meiuca.com.br
          </a>
          . Para assuntos operacionais do Sistema:{' '}
          <a
            href="mailto:suporte@meiuca.com.br"
            className="text-brand-primary hover:underline"
          >
            suporte@meiuca.com.br
          </a>
          .
        </Body>
      </section>
    </LegalPageLayout>
  )
}
